'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useHeaderCollapse } from 'src/hooks/common/useHeaderCollapse';
import useUserSettings from 'src/hooks/users/useUserSettings';
import {
  useGetProviderCohortsQuery,
  useGetProviderCohortByIdQuery,
} from 'src/api/redux/providerCohorts';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';

import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { generateSummaryString } from 'src/components/Encounters/components/EncountersDrawer/components/utils';
import { MIN_VOLUME_YEAR_STR, MAX_VOLUME_YEAR_STR } from 'src/components/constants';

// Helper to map payer type values to readable names
const getPayerDisplayName = (payerType) => {
  const payerMap = {
    medicare: 'Medicare',
    medicaid: 'Medicaid',
    commercial: 'Commercial Insurance',
    cash: 'Cash/Self-Pay',
    other: 'Other',
  };
  return payerMap[payerType.toLowerCase()] || payerType;
};

// Generate complete preview text including payer and date info
const generateCompletePreview = (
  billingCodesData,
  payerInfo,
  dateRange,
  mode
) => {
  // Get the basic medical summary
  const medicalSummary = generateSummaryString(billingCodesData, mode);

  if (medicalSummary === 'Not Available' || !medicalSummary) {
    return 'No criteria selected';
  }

  // Build the complete summary as a React node
  let payerSection = null;
  let dateSection = null;

  // Add payer information
  if (payerInfo && payerInfo.values.length > 0) {
    const payerNames = payerInfo.values.map(getPayerDisplayName);
    const payerText =
      payerNames.length === 1
        ? payerNames[0]
        : payerNames.length === 2
        ? `${payerNames[0]} ${payerInfo.operator === 'ALL' ? 'and' : 'or'} ${
            payerNames[1]
          }`
        : `${payerNames.slice(0, -1).join(', ')} ${
            payerInfo.operator === 'ALL' ? 'and' : 'or'
          } ${payerNames[payerNames.length - 1]}`;

    const billedPrefix = payerInfo.inverse ? 'not billed to' : 'billed to';
    payerSection = `. That were ${billedPrefix} ${payerText}`;
  }

  // Add date range information if available
  if (dateRange && (dateRange.gte || dateRange.lte)) {
    const startYear = dateRange.gte || MIN_VOLUME_YEAR_STR;
    const endYear = dateRange.lte || MAX_VOLUME_YEAR_STR;
    dateSection = ` from ${startYear} to ${endYear}`;
  }

  return (
    <>
      Patients {medicalSummary}
      {payerSection}
      {dateSection}.
    </>
  );
};

// Convert saved volume filters to billing codes format and extract additional info
const convertSavedVolumeToPreviewData = (appliedSavedVolume) => {
  // Handle cohorts (volumeType === 'cohort')
  if (appliedSavedVolume?.volumeType === 'cohort') {
    // Check if cohort has filters property (new format)
    if (appliedSavedVolume.filters && appliedSavedVolume.filters.length > 0) {
      // Process cohort the same way as filter sets for consistency
      const groups = [];
      let payerInfo = null;
      const dateRange = null;

      // The flattened filters don't carry modifiers, but the detailed
      // cohort_language does. Build a code -> modifier lookup so we can attach
      // modifiers to the filter-derived codes for the preview.
      const modifierByCodeId = {};
      // Check root-level codes for modifiers
      appliedSavedVolume.cohort_language?.codes?.forEach((code) => {
        if (code.modifier) {
          modifierByCodeId[`${code.code_type}-${code.code_name}`] =
            code.modifier;
        }
      });
      // Check children codes for modifiers
      appliedSavedVolume.cohort_language?.children?.forEach((childGroup) => {
        childGroup.codes?.forEach((code) => {
          if (code.modifier) {
            modifierByCodeId[`${code.code_type}-${code.code_name}`] =
              code.modifier;
          }
        });
      });

      appliedSavedVolume.filters.forEach((filter) => {
        if (filter.type === 'PAYER_TYPE' && filter.values?.length > 0) {
          payerInfo = {
            values: filter.values,
            operator: filter.is_all ? 'ALL' : 'ANY',
            inverse: filter.is_exclude || false,
          };
        } else if (
          ['icd', 'icdp', 'cpt', 'hcpcs', 'dme', 'drg', 'apc', 'drug'].some(
            (type) => filter.type.toLowerCase().startsWith(type)
          )
        ) {
          // Only include billing codes in groups
          groups.push({
            codes: filter.values.map((value) => ({
              id: value,
              context_type: filter.type,
              modifier: modifierByCodeId[value],
            })),
            operator: filter.is_all ? 'ALL' : 'ANY',
            inverse: filter.is_exclude || false,
          });
        }
      });

      // Determine the inter-group operator from cohort_language if available
      // Otherwise default to OR for multiple filter groups
      const interGroupOperator =
        appliedSavedVolume.cohort_language?.operator === 'AND' ? 'AND' : 'OR';

      return {
        groups,
        anyAllOperator: interGroupOperator,
        payerInfo,
        dateRange,
      };
    }

    // Fallback to cohort_language if filters property is not available (backward compatibility)
    // If cohort doesn't have cohort_language, return empty data
    if (!appliedSavedVolume.cohort_language) {
      return {
        groups: [],
        anyAllOperator: 'AND',
        payerInfo: null,
        dateRange: null,
      };
    }

    const groups = [];
    const payerInfo = null;
    const dateRange = null;

    // Process root-level codes (flat structure, e.g. from Claude-generated encounters)
    if (
      appliedSavedVolume.cohort_language.codes &&
      appliedSavedVolume.cohort_language.codes.length > 0
    ) {
      groups.push({
        codes: appliedSavedVolume.cohort_language.codes.map((code) => ({
          id: `${code.code_type}-${code.code_name}`,
          context_type: code.code_type,
          modifier: code.modifier,
        })),
        operator: appliedSavedVolume.cohort_language.operator === 'AND' ? 'ALL' : 'ANY',
        inverse: appliedSavedVolume.cohort_language.inverse || false,
      });
    }

    // Process cohort_language.children
    if (
      appliedSavedVolume.cohort_language.children &&
      appliedSavedVolume.cohort_language.children.length > 0
    ) {
      appliedSavedVolume.cohort_language.children.forEach((childGroup) => {
        // Process codes in each child group
        if (childGroup.codes && childGroup.codes.length > 0) {
          groups.push({
            codes: childGroup.codes.map((code) => ({
              id: `${code.code_type}-${code.code_name}`,
              context_type: code.code_type,
              modifier: code.modifier,
            })),
            operator: childGroup.operator === 'AND' ? 'ALL' : 'ANY',
            inverse: childGroup.inverse || false,
          });
        }
      });
    }

    return {
      groups,
      anyAllOperator:
        appliedSavedVolume.cohort_language.operator === 'AND' ? 'AND' : 'OR',
      payerInfo,
      dateRange,
    };
  }

  // Handle filter sets (volumeType === 'filterSet')
  if (!appliedSavedVolume?.filters || appliedSavedVolume.filters.length === 0) {
    return {
      groups: [],
      anyAllOperator: 'AND',
      payerInfo: null,
      dateRange: null,
    };
  }

  let payerInfo = null;
  const dateRange = null;

  // For filter sets, combine all filters into ONE group (matching EditCodeSetsDrawer behavior)
  const allCodes = [];
  let filterSetOperator = 'ANY'; // Default to ANY
  let isExclude = false;

  appliedSavedVolume.filters.forEach((filter) => {
    if (filter.type === 'PAYER_TYPE' && filter.values?.length > 0) {
      payerInfo = {
        values: filter.values,
        operator: filter.is_all ? 'ALL' : 'ANY',
        inverse: filter.is_exclude || false,
      };
    } else if (
      ['icd', 'icdp', 'cpt', 'hcpcs', 'dme', 'drg', 'apc', 'drug'].some(
        (type) => filter.type.toLowerCase().startsWith(type)
      )
    ) {
      // Combine all billing code filters into one group
      const codes = filter.values.map((value: string) => ({
        id: value,
        context_type: filter.type,
      }));
      allCodes.push(...codes);

      // Use the first filter's operator (assuming all filters have same operator for filter sets)
      if (allCodes.length === codes.length) {
        filterSetOperator = filter.is_all ? 'ALL' : 'ANY';
        isExclude = filter.is_exclude || false;
      }
    }
  });

  // Filter sets only have ONE group with all codes combined
  const groups =
    allCodes.length > 0
      ? [
          {
            codes: allCodes,
            operator: filterSetOperator,
            inverse: isExclude,
          },
        ]
      : [];

  return {
    groups,
    anyAllOperator: 'AND', // Filter sets always use AND
    payerInfo,
    dateRange,
  };
};

interface CodeSetPreviewProps {
  location: string;
  expandable?: boolean;
  onClose?: () => void;
  /** When true, only show preview data from the column selector, not location-level saved volume */
  columnSelectorOnly?: boolean;
}

const CodeSetPreview = ({ location, onClose, columnSelectorOnly = false }: CodeSetPreviewProps) => {
  const { appliedSavedVolume } = useSavedVolume(location);
  const { tempDateRange } = useFilterSets(location);
  const { getSetting } = useUserSettings();
  const payerMode =
    getSetting<'treating' | 'sending' | 'receiving'>('prospectMode') ||
    'treating';
  // Hook into table filter selections for column selector integration
  const { columnId, code_set, cohort_id, date_range } =
    useTableFilterSelections(location);

  // Fetch filter sets with type 'CODE' when we have a code_set ID
  const { data: filterSetsData, isLoading: codeSetLoading } =
    useGetFilterSetsQuery(
      { q: '', serializer: 'detailed', type: 'CODE' },
      { skip: !code_set }
    );

  const { data: providerCohorts } = useGetProviderCohortsQuery();
  const { isCollapsed } = useHeaderCollapse({
    threshold: 50,
  });

  // Fetch cohort data by ID when we have a cohort (from column selector or saved volume)
  const cohortId = (() => {
    // When a column is actively selected, check if it's a cohort
    if (columnId && cohort_id) return cohort_id.toString();
    // Otherwise check the location-level saved volume
    if (appliedSavedVolume?.volumeType === 'cohort')
      return appliedSavedVolume.id.toString();
    return null;
  })();

  // Only fetch cohort if the ID exists in the provider cohorts list
  const validCohortId = useMemo(() => {
    if (!cohortId || !providerCohorts?.results) return null;

    const cohortExists = providerCohorts.results.some(
      (cohort) => cohort.id.toString() === cohortId
    );

    return cohortExists ? cohortId : null;
  }, [cohortId, providerCohorts]);

  const { data: cohortData } = useGetProviderCohortByIdQuery(validCohortId, {
    skip: !validCohortId,
  });

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isOpen && isCollapsed) {
      setIsOpen(false);
    }
  }, [isOpen, isCollapsed]);

  // Get selected column data based on column selector
  const selectedColumnData = useMemo(() => {
    if (!columnId) return null;

    if (code_set && filterSetsData?.results) {
      // Find the code set by ID
      const codeSet = filterSetsData?.results.find(
        (set) => set.id === code_set
      );
      if (codeSet) {
        return {
          id: codeSet.id,
          label: codeSet.label,
          type: 'CODE',
          scope: codeSet.scope,
          owner: codeSet.owner,
          filters: codeSet.filters,
          created_at: codeSet.created_at,
          status: codeSet.status,
          volumeType: 'filterSet' as const,
        };
      }
    }

    if (cohort_id && providerCohorts?.results) {
      // Find the provider cohort by ID
      const cohort = providerCohorts.results.find(
        (cohort) => cohort.id === cohort_id
      );
      if (cohort) {
        return {
          id: cohort.id,
          label: cohort.label,
          status: cohort.status,
          volumeType: 'cohort' as const,
        };
      }
    }

    return null;
  }, [columnId, code_set, cohort_id, filterSetsData, providerCohorts]);

  // Convert and generate dynamic preview
  // When a column is actively selected via the column selector dropdown,
  // use that column's data. Otherwise fall back to the location-level saved volume
  // (used on discovery page and other non-column contexts).
  const previewData = useMemo(() => {
    const dataSource = columnId
      ? selectedColumnData
      : columnSelectorOnly
        ? null
        : appliedSavedVolume;

    // If we have a cohort and valid cohort data from the API, merge in detailed cohort_language
    if (
      dataSource?.volumeType === 'cohort' &&
      validCohortId &&
      cohortData
    ) {
      const mergedCohortData = {
        ...dataSource,
        cohort_language: cohortData.cohort_language,
      };
      return convertSavedVolumeToPreviewData(mergedCohortData);
    }

    // If dataSource is a cohort but invalid/not found, show empty state
    if (dataSource?.volumeType === 'cohort' && !validCohortId) {
      return convertSavedVolumeToPreviewData(null);
    }

    return convertSavedVolumeToPreviewData(dataSource);
  }, [columnId, columnSelectorOnly, appliedSavedVolume, selectedColumnData, cohortData, validCohortId]);

  const summaryText = useMemo(() => {
    // If code sets are still loading, show loading message
    if (codeSetLoading) return 'Loading...';

    const hasData = previewData.groups.some((group) => group.codes.length > 0);
    if (!hasData) return 'No criteria selected';

    // Use date_range from column selections when a column is active, otherwise use tempDateRange
    const activeDateRange = columnId ? date_range : tempDateRange;

    return generateCompletePreview(
      {
        groups: previewData.groups,
        anyAllOperator: previewData.anyAllOperator,
      },
      previewData.payerInfo,
      activeDateRange,
      payerMode
    );
  }, [previewData, date_range, columnId, tempDateRange, payerMode]);

  return (
    <div className="w-full" data-testid="code-set-preview">
      <div className="flex justify-between items-center flex-row pb-2">
        <div
          className="text-primary text-sm font-semibold font-['Inter'] leading-normal"
          data-testid="code-set-preview-title"
        >
          Preview Search
        </div>
        {onClose && (
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0"
            onClick={onClose}
            aria-label="Close code set preview"
            data-testid="code-set-preview-close"
          >
            <Icon icon={faClose} size="sm" className="w-4 h-4 text-tertiary" />
          </button>
        )}
      </div>
      <div
        className="self-stretch text-primary text-sm font-normal font-['Inter'] leading-normal break-words"
        aria-live="polite"
        aria-atomic="true"
        data-testid="code-set-preview-summary"
      >
        {summaryText}
      </div>
    </div>
  );
};

export default CodeSetPreview;
